import * as stylex from "@stylexjs/stylex";
import { colors, fonts } from "@softmax/design-system/tokens.stylex";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { ExternalMark, externalLinkProps } from "@softmax/web-ux/shell";
import { SidebarIcon } from "./sidebar-icon";
import { sidebarLinkStyles } from "./sidebar-link.styles";

export type SidebarItem = {
  readonly key:
    | "browse"
    | "watch"
    | "play"
    | "policies"
    | "forum"
    | "wiki"
    | "docs";
  readonly label: string;
  readonly href: string;
  readonly active: boolean;
  readonly current: boolean;
  readonly external?: boolean;
  readonly badge?: number | undefined;
};

export type SidebarLinkProps = Readonly<
  AnchorHTMLAttributes<HTMLAnchorElement>
> & {
  readonly href: string;
  readonly "data-active"?: boolean | undefined;
};

export function SidebarNavItem({
  item,
  renderLink,
}: {
  readonly item: SidebarItem;
  readonly renderLink: (props: SidebarLinkProps) => ReactNode;
}) {
  const content = (
    <>
      <SidebarIcon name={item.key} active={item.active} />
      {item.external ? (
        <span>
          {item.label}
          <ExternalMark />
        </span>
      ) : (
        item.label
      )}
      {item.badge === undefined ? null : (
        <span {...stylex.props(styles.badge)}>{item.badge}</span>
      )}
    </>
  );
  const props: SidebarLinkProps = {
    href: item.href,
    "aria-current": item.current ? "page" : undefined,
    "data-active": item.active || undefined,
    ...stylex.props(
      sidebarLinkStyles.row,
      item.active ? sidebarLinkStyles.active : sidebarLinkStyles.resting,
    ),
    children: content,
  };
  return item.external ? (
    <a {...props} {...externalLinkProps}>
      {content}
    </a>
  ) : (
    renderLink(props)
  );
}

const styles = stylex.create({
  badge: {
    marginLeft: "auto",
    fontFamily: fonts.sans,
    fontSize: 12,
    lineHeight: "15.6px",
    fontWeight: 400,
    letterSpacing: "0.3px",
    color: colors.muted,
    fontVariantNumeric: "tabular-nums",
  },
});
